import { subject } from '@casl/ability';
import { arrayMove } from '@dnd-kit/sortable';
import Box from '@mui/material/Box';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { isValid, isWithinInterval, parseISO } from 'date-fns';
import { get, isObject, orderBy, uniq } from 'lodash-es';
import type { IntlShape } from 'react-intl';
import { getAuditStatusColor } from './colors';
import { getAuditStatusIcon } from './icons';
import ability from 'casl/ability';
import Icon from 'components/Icon';
import { type ErrorObject, type Group, type ISO8601DateOnlyString, type Organisation, type Period, type User } from 'types/models';
import { ApprovalStatus, KPIFieldType, type KPIFieldTypeEnum } from 'utils/enum';

const unitRequiringFields = [
   KPIFieldType.WEIGHT,
   KPIFieldType.SURFACE,
   KPIFieldType.MEASUREMENT,
   KPIFieldType.EMISSIONS,
   KPIFieldType.EMISSION_SELECT,
   KPIFieldType.DISTANCE,
   KPIFieldType.ELECTRICITY,
   KPIFieldType.VOLUME,
   KPIFieldType.WOOD,
   KPIFieldType.TIME,
   KPIFieldType.PERCENTAGE,
   KPIFieldType.POWER,
   KPIFieldType.PRICE,
   KPIFieldType.CONTAINER_DISTANCE,
   KPIFieldType.DATA_TIME,
   KPIFieldType.DATA,
   KPIFieldType.NUMBER_TIME,
   KPIFieldType.PASSENGER_DISTANCE,
   KPIFieldType.WEIGHT_TIME,
   KPIFieldType.WEIGHT_DISTANCE,
   KPIFieldType.SURFACE_TIME,
] as const;

export type KPIFieldTypeRequiringUnit = (typeof unitRequiringFields)[number];

export function fieldTypeRequiresUnit(kpiFieldType?: KPIFieldTypeEnum): boolean {
   if (!kpiFieldType) {
      return false;
   }

   return (unitRequiringFields as readonly KPIFieldTypeEnum[]).includes(kpiFieldType);
}

export function fieldTypeCanHaveValidationRules(kpiFieldType?: KPIFieldTypeEnum): boolean {
   if (!kpiFieldType) {
      return false;
   }

   const acceptedFieldTypes = [
      KPIFieldType.WEIGHT,
      KPIFieldType.SURFACE,
      KPIFieldType.PRICE,
      KPIFieldType.MEASUREMENT,
      KPIFieldType.EMISSIONS,
      KPIFieldType.EMISSION_SELECT,
      KPIFieldType.DISTANCE,
      KPIFieldType.ELECTRICITY,
      KPIFieldType.VOLUME,
      KPIFieldType.PERCENTAGE,
      KPIFieldType.NUMBER,
      KPIFieldType.DATA,
      KPIFieldType.FORMULA,
      KPIFieldType.WOOD,
      KPIFieldType.TIME,
      KPIFieldType.TEXT,
      KPIFieldType.TEXTAREA,
      KPIFieldType.SWITCH,
   ] as const;
   return (acceptedFieldTypes as readonly KPIFieldTypeEnum[]).includes(kpiFieldType);
}

export function fieldTypeAllowsUnit(kpiFieldType?: keyof typeof KPIFieldType): boolean {
   if (!kpiFieldType) {
      return false;
   }

   return fieldTypeRequiresUnit(kpiFieldType) || kpiFieldType === KPIFieldType.FORMULA;
}

export { numericFields, fieldTypeIsNumeric } from '../kpiFieldTypes';

export function canAccessOrganisation(organisationId: number) {
   return ability.can('read', subject('organisation', { organisationId }));
}

export async function handleDrag(active: any, over: any): Promise<void> {
   if (active?.data?.current?.type && Array.isArray(over?.data?.current?.accepts) && over.data.current.accepts.includes(active.data.current.type)) {
      let parentKey: string | undefined;
      let collectionName: string | undefined;
      let parentEntityName: string | undefined;
      let parentId: string | undefined;

      switch (active.data.current.type) {
         case 'footprint_footprintSub':
            parentKey = 'footprintId';
            collectionName = 'subs';
            parentEntityName = 'emissions/ghg/footprints';
            parentId = active?.data?.current?.additionalAttributes?.parentId;
            break;
         case 'liveTemplate_footprintSub':
            parentKey = 'fpTemplateId';
            collectionName = 'subs';
            parentEntityName = 'emissions/ghg/fptemplates';
            parentId = active?.data?.current?.additionalAttributes?.parentId;
            break;
         case 'fpCalculation':
            parentKey = 'footprintSubId';
            collectionName = 'calculations';
            parentEntityName = 'emissions/ghg/footprintssubs';
            parentId = active?.data?.current?.additionalAttributes?.parentId;
            break;
         case 'fpCalculationField':
            parentKey = 'fpCalculationId';
            collectionName = 'fields';
            parentEntityName = 'emissions/ghg/calculations';
            parentId = active?.data?.current?.additionalAttributes?.parentId;
            break;
         case 'kpi':
            parentKey = 'kpiAreaId';
            collectionName = 'kpis';
            parentEntityName = 'kpiAreas';
            break;
         case 'kpiSub':
            parentKey = 'kpiId';
            collectionName = 'kpisubs';
            parentEntityName = 'kpis';
            break;
         case 'kpiContent':
            parentKey = 'kpiSubId';
            collectionName = 'kpicontents';
            parentEntityName = 'kpisubs';
            break;
         case 'kpiField':
            parentKey = 'kpiContentId';
            collectionName = 'kpifields';
            parentEntityName = 'kpicontents';
            break;
         default:
            break;
      }

      // rearrange in the same container
      if (active.id !== over.id && active.data.current.sortable.containerId === over.data.current.sortable.containerId) {
         const oldIndex = active.data.current.sortable.items.indexOf(active.id);
         const newIndex = over.data.current.sortable.items.indexOf(over.id);

         active.data.current.api.sortEntities(
            arrayMove(active.data.current.sortable.items, oldIndex, newIndex),
            parentEntityName!,
            `${active.data.current.type}Id` === parentKey ? active.id : active.data.current.sortable.containerId,
            collectionName!,
            parentId
         );
         // move to a different container
      } else if (active.data.current.sortable.containerId !== over.data.current.sortable.containerId) {
         await active.data.current.api.updateEntity(active.id, {
            [parentKey!]: `${over.data.current.type}Id` === parentKey ? over.id : over.data.current.sortable.containerId,
         });
         over.data.current.api.sortEntities(
            over.data.current.sortable.items.splice(over.data.current.sortable.index, 0, active.id),
            parentEntityName!,
            `${over.data.current.type}Id` === parentKey ? over.id : over.data.current.sortable.containerId,
            collectionName!,
            parentId
         );
         active.data.current.api.sortEntities(
            active.data.current.sortable.items.splice(active.data.current.sortable.items.indexOf(active.id), 1),
            parentEntityName!,
            `${active.data.current.type}Id` === parentKey ? active.id : active.data.current.sortable.containerId,
            collectionName!,
            parentId
         );
      }
   }
}

type Parent = {
   validFrom?: ISO8601DateOnlyString;
   validTo?: ISO8601DateOnlyString | null;
   [key: string]: any; // Allow additional properties if needed
};

type OrgParentWithValidation = Parent & {
   isValidFromInPeriod: boolean;
   isValidToInPeriod: boolean;
   validFromEqualOrBefore: boolean;
   validToEqualAfterPeriod: boolean;
   notValidForPeriod: boolean;
   noValidationDateValidFrom: boolean;
   noValidationDateValidTo: boolean;
};

export function getCorrectOrganisationParent(organisation: Organisation, period: Partial<Period>): OrgParentWithValidation[] {
   const parentValidPeriod = (organisation.parents ?? []).flatMap((_parent) => [_parent?.validFrom, _parent?.validTo]);

   if (parentValidPeriod.length === 0) {
      return [];
   }

   if (period?.from && period?.to) {
      const currentPeriodFrom = parseISO(period.from);
      const currentPeriodTo = parseISO(period.to);

      if (parentValidPeriod.length > 0) {
         const checkIfParentIsInPeriod: OrgParentWithValidation[] = [];

         (organisation.parents ?? []).forEach((parent) => {
            let isValidFromInPeriod = false;
            let isValidToInPeriod = false;
            let validFromEqualOrBefore = false;
            let validToEqualAfterPeriod = false;
            let noValidationDateValidFrom = false;
            let noValidationDateValidTo = false;
            let notValidForPeriod = false;

            const from = parent?.validFrom && isValid(parseISO(parent?.validFrom)) ? parseISO(parent.validFrom) : null;
            const to = parent?.validTo && isValid(parseISO(parent?.validTo)) ? parseISO(parent.validTo) : null;

            if (!from || !to) {
               noValidationDateValidFrom = from === null;
               noValidationDateValidTo = to === null;
            }

            if (from) {
               validFromEqualOrBefore = from <= currentPeriodFrom;
               isValidFromInPeriod = isWithinInterval(from, {
                  start: currentPeriodFrom,
                  end: currentPeriodTo,
               });
               if (!validFromEqualOrBefore && !isValidFromInPeriod && !to) {
                  notValidForPeriod = true;
               }
            }

            if (to) {
               validToEqualAfterPeriod = to >= currentPeriodTo;
               isValidToInPeriod = isWithinInterval(to, {
                  start: currentPeriodFrom,
                  end: currentPeriodTo,
               });
               if (!validToEqualAfterPeriod && !isValidToInPeriod && !from) {
                  notValidForPeriod = true;
               }
            }

            if (
               ((!validToEqualAfterPeriod && !isValidToInPeriod) || (!validFromEqualOrBefore && !isValidFromInPeriod)) &&
               !noValidationDateValidFrom &&
               !noValidationDateValidTo
            ) {
               notValidForPeriod = true;
            }

            checkIfParentIsInPeriod.push({
               ...parent,
               isValidFromInPeriod,
               isValidToInPeriod,
               validFromEqualOrBefore,
               validToEqualAfterPeriod,
               notValidForPeriod,
               noValidationDateValidFrom,
               noValidationDateValidTo,
            });
         });

         return checkIfParentIsInPeriod;
      }
   }

   return [];
}

interface OrganisationObject {
   id?: string | number;
   code?: string;
   name?: string;
}

export function formatOrganisationName(
   organisation: Organisation,
   period: Partial<Period>,
   intl: IntlShape,
   organisations: OrganisationObject[],
   viewType: string,
   showCode = true
) {
   if ((!period?.from && !period?.to) || organisations.length === 0) {
      return '';
   }
   if ((organisation?.parents ?? []).length === 0) {
      return organisation.code && showCode ? `${organisation.code}: ${organisation.name}` : organisation.name;
   }
   const organisationLabel = organisation.code && showCode ? `${organisation.code}: ${organisation.name}` : `${organisation.name}`;

   const parentsPerPeriod = getCorrectOrganisationParent(organisation, period);

   const parentsValidForPeriod = parentsPerPeriod.filter(
      ({
         validFromEqualOrBefore,
         validToEqualAfterPeriod,
         noValidationDateValidFrom,
         isValidFromInPeriod,
         noValidationDateValidTo,
         isValidToInPeriod,
      }) =>
         (validFromEqualOrBefore || noValidationDateValidFrom || isValidFromInPeriod) &&
         (validToEqualAfterPeriod || noValidationDateValidTo || isValidToInPeriod)
   );

   if (parentsValidForPeriod.length === 1) {
      const {
         isValidFromInPeriod,
         isValidToInPeriod,
         validFromEqualOrBefore,
         validToEqualAfterPeriod,
         noValidationDateValidFrom,
         noValidationDateValidTo,
         validFrom,
         validTo,
         subsidiaryProportion,
      } = parentsValidForPeriod[0];

      const formattedProportion = intl.formatNumber(subsidiaryProportion ?? 0, {
         maximumFractionDigits: 5,
         style: 'percent',
      });

      if ((validFromEqualOrBefore || noValidationDateValidFrom) && (validToEqualAfterPeriod || noValidationDateValidTo)) {
         let formattedLabel = `${formattedProportion}`;
         if (viewType === 'row') {
            formattedLabel = `${organisationLabel} (${formattedProportion})`;
         }
         return <Typography> {formattedLabel} </Typography>;
      } else if (isValidFromInPeriod || isValidToInPeriod) {
         let validText = '';
         if (noValidationDateValidTo && isValidFromInPeriod) {
            validText = `${intl.formatMessage({
               id: 'organisations.validFrom',
               defaultMessage: 'valid from:',
            })} ${parseISO(validFrom!).toLocaleDateString()}`;
         } else if (isValidToInPeriod && noValidationDateValidFrom) {
            validText = `${intl.formatMessage({
               id: 'organisations.validTo',
               defaultMessage: 'valid to:',
            })} ${parseISO(validTo!).toLocaleDateString()}`;
         } else {
            validText = `${parseISO(validFrom!).toLocaleDateString()} - ${parseISO(validTo!).toLocaleDateString()}`;
         }
         return (
            <Typography>
               {viewType === 'row' ? organisationLabel : ''}
               {`${formattedProportion} (${validText})`}
            </Typography>
         );
      }
   } else {
      let parentsArray = parentsValidForPeriod;
      if (parentsValidForPeriod.length === 0 && parentsPerPeriod.length > 0) {
         parentsArray = parentsPerPeriod;
      }

      parentsArray = orderBy(parentsArray, ['validFrom', 'validTo'], ['asc', 'asc']);
      const uniqParentIds = uniq(Array.from(parentsArray.map((parent) => parent?.id)));

      return (
         <>
            {viewType === 'row' && (
               <Typography
                  sx={{
                     paddingBottom: parentsArray.length > 0 ? 1 : 0,
                     fontSize: 14
                  }}>
                  {organisationLabel}
               </Typography>
            )}
            {(parentsArray ?? []).map((parent, index) => {
               const {
                  validFromEqualOrBefore,
                  validToEqualAfterPeriod,
                  noValidationDateValidFrom,
                  noValidationDateValidTo,
                  validFrom,
                  validTo,
                  isValidFromInPeriod,
                  isValidToInPeriod,
                  notValidForPeriod,
               } = parent;

               let parentName = '';
               if (uniqParentIds.length > 1) {
                  const parentObject = (organisations ?? []).find((org) => org?.id === parent?.id);
                  parentName = parentObject?.code && showCode ? `${parentObject?.code}: ${parentObject?.name}` : `${parentObject?.name}`;
               }
               const formattedProportion = intl.formatNumber(parent?.subsidiaryProportion ?? 0, {
                  maximumFractionDigits: 2,
                  style: 'percent',
               });

               if ((validFromEqualOrBefore || noValidationDateValidFrom) && (validToEqualAfterPeriod || noValidationDateValidTo)) {
                  return (
                     <Typography
                        key={`${parent?.id}_${index}`}
                        sx={{
                           paddingBottom: 1,
                           fontSize: 12
                        }}>
                        {`${formattedProportion} (${parentName})`}
                     </Typography>
                  );
               } else if (isValidFromInPeriod || isValidToInPeriod || notValidForPeriod) {
                  let validText = '';
                  if ((noValidationDateValidTo && isValidFromInPeriod) || (noValidationDateValidTo && notValidForPeriod)) {
                     validText = `${intl.formatMessage({
                        id: 'organisations.validFrom',
                        defaultMessage: 'valid from:',
                     })} ${parseISO(validFrom!).toLocaleDateString()}`;
                  } else if ((noValidationDateValidFrom && isValidToInPeriod) || (noValidationDateValidFrom && notValidForPeriod)) {
                     validText = `${intl.formatMessage({
                        id: 'organisations.validTo',
                        defaultMessage: 'valid to:',
                     })} ${parseISO(validTo!).toLocaleDateString()}`;
                  } else {
                     validText = `${parseISO(validFrom!).toLocaleDateString()} - ${parseISO(validTo!).toLocaleDateString()}`;
                  }

                  const formattedProportionPeriod = `${formattedProportion} (${validText}) ${parentName}`;
                  return (
                     <Typography
                        key={`${parent?.id}_${index}`}
                        sx={{
                           paddingBottom: 1,
                           fontSize: 12
                        }}>
                        {formattedProportionPeriod}
                     </Typography>
                  );
               }
            })}
         </>
      );
   }
}

type TransformInput = {
   allowedAuditors?: User[];
   allowedEditors?: User[];
   allowedAuditorGroups?: Group[];
   allowedEditorGroups?: Group[];
   [key: string]: unknown; // Allow additional properties if necessary
};

type TransformOutput = Omit<TransformInput, 'allowedAuditors' | 'allowedEditors' | 'allowedAuditorGroups' | 'allowedEditorGroups'> & {
   allowedAuditors: number[]; // IDs only
   allowedEditors: number[];
   allowedAuditorGroups: number[]; // Flattened group IDs
   allowedEditorGroups: number[];
};

// Fully typed function
export function transformAllowedAuditorsAndEditorsToIds(object: TransformInput): TransformOutput {
   const allowedAuditorIds = (object?.allowedAuditors ?? []).map((auditor) => auditor.id) ?? [];
   const allowedEditorIds = (object?.allowedEditors ?? []).map((editor) => editor.id) ?? [];
   const allowedAuditorGroupUserIds =
      (object?.allowedAuditorGroups ?? []).flatMap((group) => (group?.users ?? []).map((auditor) => auditor.id)) ?? [];
   const allowedEditorGroupUserIds = (object?.allowedEditorGroups ?? []).flatMap((group) => (group?.users ?? []).map((editor) => editor.id)) ?? [];

   return {
      ...object,
      allowedAuditors: allowedAuditorIds,
      allowedEditors: allowedEditorIds,
      allowedAuditorGroups: allowedAuditorGroupUserIds ?? [],
      allowedEditorGroups: allowedEditorGroupUserIds ?? [],
   };
}

export function determineAuditStatusTooltipTitle(intl: IntlShape, object: any) {
   if (!object) {
      return '';
   }

   const { status, approver, reviewer } = object;
   if (status === ApprovalStatus.APPROVED && approver && reviewer) {
      return intl.formatMessage(
         {
            id: 'common.approvedBy',
            defaultMessage: 'Status: {status} {br} Approved by {approver} {br} Reviewed by {reviewer}',
         },
         {
            approver: `${approver?.firstName} ${approver?.lastName}`,
            reviewer: `${reviewer?.firstName} ${reviewer?.lastName}`,
            br: <br />,
            status: intl.formatMessage({
               id: `overview.tabs.status.${status}`,
               defaultMessage: (status ?? '').toLowerCase(),
            }),
         }
      );
   } else if (status === ApprovalStatus.REVIEWED && reviewer) {
      return intl.formatMessage(
         {
            id: 'common.reviewedBy',
            defaultMessage: 'Status: {status} {br} Reviewed by {reviewer}',
         },
         {
            reviewer: `${reviewer?.firstName} ${reviewer?.lastName}`,
            br: <br />,
            status: intl.formatMessage({
               id: `overview.tabs.status.${status}`,
               defaultMessage: (status ?? '').toLowerCase(),
            }),
         }
      );
   } else {
      return intl.formatMessage(
         {
            id: `overview.tabs.status.tooltip`,
            defaultMessage: 'Status: {status}',
         },
         {
            status: intl.formatMessage({
               id: `overview.tabs.status.${status}`,
               defaultMessage: (status ?? '').toLowerCase(),
            }),
         }
      );
   }
}

export function getAuditStatusNode(intl: IntlShape, palette: any, object: any, fontSize = 20, removePadding = false) {
   return (
      <Tooltip title={determineAuditStatusTooltipTitle(intl, object)}>
         <Box component="span" sx={{
            display: "inline-flex"
         }}>
            <Icon
               icon={getAuditStatusIcon(object?.status)}
               fontSize={fontSize}
               style={{ color: get(palette, getAuditStatusColor(object?.status)), padding: removePadding ? 0 : undefined }}
            />
         </Box>
      </Tooltip>
   );
}

export function formatErrors(errorList: ErrorObject[], intl: IntlShape) {
   const sanitizedErrorList = errorList.filter((err) => isObject(err));

   if (sanitizedErrorList.length === 0) {
      return null;
   }

   return (
      <>
         <Typography variant="inherit">
            {intl.formatMessage({ id: 'errors.conversionTitle', defaultMessage: 'Errors occurred while calculating:' })}
         </Typography>
         <Box component="ul" sx={{ pl: 2, m: 0 }}>
            {sanitizedErrorList.map((errObj, index) => (
               <li key={`${index}_${errObj?.kind ?? ''}`}>
                  <Typography variant="inherit">
                     {intl.formatMessage(
                        { id: `errors.${errObj?.error ?? 'unknown_error'}`, defaultMessage: errObj?.error ?? '' },
                        {
                           ...errObj,
                           ...(errObj?.kind
                              ? {
                                   kind: intl.formatMessage({
                                      id: `entityKind.${errObj?.kind}`,
                                      defaultMessage: errObj?.kind,
                                   }),
                                }
                              : {}),
                        }
                     )}
                  </Typography>
               </li>
            ))}
         </Box>
      </>
   );
}
